import { recommendationProductAction } from "./product-action.mjs";
import {
	hasMinimumRecommendations,
	parseRecentProductIds,
	recommendationRequest,
} from "./recommendation-context.mjs";

function readCookie(name) {
	const prefix = `${name}=`;
	return (
		document.cookie
			.split(";")
			.map((value) => value.trim())
			.find((value) => value.startsWith(prefix))
			?.slice(prefix.length) || ""
	);
}

function scalar(value) {
	if (Array.isArray(value)) {
		return scalar(value[0]);
	}
	return value === null || value === undefined ? "" : String(value);
}

function hitValue(hit, keys) {
	const attributes = hit?.attributes && typeof hit.attributes === "object" ? hit.attributes : {};
	for (const key of keys) {
		const value = scalar(attributes[key] ?? hit?.[key]);
		if (value) return value;
	}
	return "";
}

function hitValues(hit, keys) {
	const attributes = hit?.attributes && typeof hit.attributes === "object" ? hit.attributes : {};
	for (const key of keys) {
		const value = attributes[key] ?? hit?.[key];
		if (Array.isArray(value)) return value.map(scalar).filter(Boolean);
		const normalized = scalar(value);
		if (normalized) return [normalized];
	}
	return [];
}

function productImage(hit) {
	const attributes = hit?.attributes && typeof hit.attributes === "object" ? hit.attributes : {};
	const images = Array.isArray(attributes.images) ? attributes.images : [];
	const image = images.find((item) => item && typeof item === "object") || {};
	return {
		url: scalar(image.thumbnail || image.src || attributes.image_link || hit?.image_link),
		alt: scalar(image.alt),
		srcset: scalar(image.srcset),
		sizes: scalar(image.sizes),
	};
}

function resourceIdentifier(url, fallback = "") {
	if (!url) return fallback;
	try {
		const parsed = new URL(url, window.location.origin);
		return `${parsed.pathname}${parsed.search}`;
	} catch {
		return fallback;
	}
}

function productFromHit(hit, position) {
	const url = hitValue(hit, ["url", "link"]);
	const resourceId = resourceIdentifier(url, hitValue(hit, ["item_id", "id", "sku"]));
	const image = productImage(hit);
	const rawPriceAmount = hitValue(hit, ["price_amount", "price"]);
	const priceAmount = rawPriceAmount === "" ? null : Number(rawPriceAmount);
	return {
		resourceId,
		productId: hitValue(hit, ["id", "item_id"]),
		sku: hitValue(hit, ["sku"]),
		productType: hitValue(hit, ["product_type"]),
		availability: hitValue(hit, ["availability"]),
		categories: hitValues(hit, ["category", "categories"]),
		title: hitValue(hit, ["title.untouched", "title", "name"]),
		url,
		image,
		price: hitValue(hit, ["price_formatted", "price_amount", "price"]),
		priceAmount: Number.isFinite(priceAmount) ? priceAmount : null,
		position,
	};
}

function cloneIcon(root, selector) {
	const template = root.querySelector(selector);
	return template?.content?.firstElementChild?.cloneNode(true) || null;
}

function applyLuigiClickData(element, product, metadata) {
	element.dataset.luigiResource = product.resourceId;
	element.dataset.luigiListName = "Recommendation";
	element.dataset.luigiActionType = "click";
	element.dataset.luigiItemTitle = product.title;
	element.dataset.luigiRecommendationId = metadata.recommendationId;
}

function productButton(root, product, metadata, config, wooAction) {
	const action = recommendationProductAction(product, wooAction);
	const button = document.createElement("a");
	button.className = [
		config.button_classes || "",
		"button mx-auto mt-5 flex gap-1 flux-luigi-client__button",
		...action.classes,
	].filter(Boolean).join(" ");
	button.href = action.href;
	applyLuigiClickData(button, product, metadata);
	button.dataset.quantity = "1";
	button.dataset.product_id = action.productId;
	button.dataset.product_sku = action.productSku;
	button.dataset.productName = action.productName;
	if (action.productPrice !== "") button.dataset.productPrice = action.productPrice;
	if (action.productCategory) button.dataset.productCategory = action.productCategory;

	const buttonText = document.createElement("span");
	buttonText.dataset.addToCartText = "";
	buttonText.textContent = action.text;
	button.append(buttonText);
	const cartIcon = cloneIcon(root, "[data-luigi-client-cart-icon]");
	const optionsIcon = cloneIcon(root, "[data-luigi-client-options-icon]");
	if (cartIcon) button.append(cartIcon);
	if (optionsIcon) button.append(optionsIcon);
	return button;
}

function productCard(root, product, metadata, config, position) {
	const card = document.createElement("article");
	card.className = "flux-luigi-client__card";
	card.dataset.luigiItemId = product.resourceId;

	const imageLink = document.createElement("a");
	imageLink.className = "flux-luigi-client__image-link";
	imageLink.href = product.url || "#";
	imageLink.setAttribute("aria-label", `View ${product.title}`);
	applyLuigiClickData(imageLink, product, metadata);

	const imageWrapper = document.createElement("span");
	imageWrapper.className = "flux-luigi-client__image-wrapper";

	const image = document.createElement("img");
	image.className = "flux-luigi-client__image";
	image.src = product.image.url;
	image.alt = product.image.alt || product.title;
	image.width = 300;
	image.height = 300;
	if (product.image.srcset) image.srcset = product.image.srcset;
	if (product.image.sizes) image.sizes = product.image.sizes;
	image.loading = "lazy";
	image.decoding = "async";
	imageWrapper.append(image);
	imageLink.append(imageWrapper);

	const content = document.createElement("div");
	content.className = "flux-luigi-client__content";
	const details = document.createElement("div");
	details.className = "flux-luigi-client__details";

	const title = document.createElement("a");
	title.className = "flux-luigi-client__title";
	title.href = imageLink.href;
	title.textContent = product.title;
	applyLuigiClickData(title, product, metadata);

	const price = document.createElement("div");
	price.className = "flux-luigi-client__price";
	price.textContent = Number.isFinite(product.priceAmount)
		? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(product.priceAmount)
		: product.price;

	const actionPlaceholder = document.createElement("span");
	actionPlaceholder.className = "flux-luigi-client__action-placeholder";
	actionPlaceholder.dataset.luigiClientAction = String(position);
	actionPlaceholder.setAttribute("aria-hidden", "true");

	details.append(title, price);
	content.append(details, actionPlaceholder);
	card.append(imageLink, content);
	return card;
}

async function requestProductActions(products, config) {
	if (!config.action_endpoint) return new Map();

	try {
		const response = await fetch(config.action_endpoint, {
			method: "POST",
			credentials: "same-origin",
			headers: { "Content-Type": "application/json; charset=utf-8" },
			body: JSON.stringify({
				products: products.map((product, index) => ({
					request_id: String(index),
					item_id: product.productId,
					sku: product.sku,
					url: product.url,
				})),
			}),
		});
		if (!response.ok) return new Map();

		const payload = await response.json();
		return new Map(
			(Array.isArray(payload?.actions) ? payload.actions : [])
				.map((action) => [String(action?.request_id ?? ""), action]),
		);
	} catch {
		return new Map();
	}
}

function wooProductFromAction(action, position) {
	const display = action?.display && typeof action.display === "object" ? action.display : {};
	const image = display?.image && typeof display.image === "object" ? display.image : {};
	const url = scalar(display.url || action?.href);
	return {
		resourceId: resourceIdentifier(url, scalar(action?.product_id || action?.product_sku)),
		productId: scalar(action?.product_id),
		sku: scalar(action?.product_sku),
		productType: scalar(action?.product_type),
		availability: action?.in_stock ? "1" : "0",
		categories: action?.category ? [scalar(action.category)] : [],
		title: scalar(display.title || action?.product_name),
		url,
		image: {
			url: scalar(image.url),
			alt: scalar(image.alt),
			srcset: scalar(image.srcset),
			sizes: scalar(image.sizes),
		},
		price: scalar(display.price),
		priceAmount: null,
		position,
	};
}

async function loadRecentProductFallback(config) {
	const cookieName = scalar(config.recent_products_cookie);
	if (!cookieName) return { products: [], actions: new Map() };

	const configuredProductIds = Array.isArray(config.recent_product_ids)
		? config.recent_product_ids.join(",")
		: "";
	const productIds = parseRecentProductIds(
		configuredProductIds || readCookie(cookieName),
		config.current_product_id,
		config.size,
	);
	if (!hasMinimumRecommendations(productIds, config.minimum_items)) {
		return { products: [], actions: new Map() };
	}

	const actions = await requestProductActions(productIds.map((productId, index) => ({
		requestId: String(index),
		productId,
		sku: "",
		url: "",
	})), config);
	const products = productIds.map((productId, index) => {
		const action = actions.get(String(index));
		return action?.matched ? wooProductFromAction(action, index + 1) : null;
	}).filter((product) => (
		product?.resourceId && product.title && product.url && product.image.url
	));

	return { products, actions };
}

function analyticsMetadata(response, config) {
	return {
		recommendationId: scalar(response?.recommendation_id || response?.recommendationId),
		recommender: scalar(response?.recommender),
		type: scalar(response?.recommendation_type) || config.recommendation_type,
		clientId: scalar(response?.recommender_client_identifier) || config.recommender_client_identifier,
	};
}

function trackRecommendationView(products, metadata) {
	if (typeof window.fluxTrackLuigiEvent !== "function") return;
	window.fluxTrackLuigiEvent("list", {
		listName: "Recommendation",
		items: products.map((product) => ({
			title: product.title,
			type: "item",
			url: product.resourceId,
			position: product.position,
			price: product.price,
		})),
		filters: {
			RecommendationId: metadata.recommendationId,
			RecommenderClientId: metadata.clientId,
			Recommender: metadata.recommender,
			Type: metadata.type,
		},
	});
}

function updateSliderControls(root) {
	const track = root.querySelector("[data-luigi-client-track]");
	const previous = root.querySelector("[data-luigi-client-prev]");
	const next = root.querySelector("[data-luigi-client-next]");
	if (!track || !previous || !next) return;

	const edgeTolerance = 2;
	const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
	previous.disabled = track.scrollLeft <= edgeTolerance;
	next.disabled = maximumScroll <= edgeTolerance
		|| track.scrollLeft >= maximumScroll - edgeTolerance;
}

async function loadRecommendations(root) {
	const config = JSON.parse(root.dataset.config || "{}");
	const clientId = readCookie("_lb") || readCookie("_lb_id");
	let products = [];
	let productActions = null;
	let metadata = analyticsMetadata({}, config);

	if (!config.require_user_id || clientId) {
		try {
			const response = await fetch(config.endpoint, {
				method: "POST",
				headers: { "Content-Type": "application/json; charset=utf-8" },
				body: JSON.stringify([recommendationRequest(config, clientId)]),
			});
			if (!response.ok) throw new Error(`Luigi recommendation request failed (${response.status})`);

			const payload = await response.json();
			const result = Array.isArray(payload) ? payload[0] : payload;
			const blacklisted = new Set([
				...(Array.isArray(config.blacklisted_item_ids) ? config.blacklisted_item_ids : []),
				scalar(config.current_product_id),
			].map(scalar).filter(Boolean));
			products = (Array.isArray(result?.hits) ? result.hits : [])
				.map((hit, index) => productFromHit(hit, index + 1))
				.filter((product) => (
					product.resourceId
					&& product.title
					&& product.url
					&& product.image.url
					&& ![product.resourceId, product.productId, product.sku].some((value) => blacklisted.has(value))
				));
			metadata = analyticsMetadata(result, config);
		} catch {
			products = [];
		}
	}

	if (!hasMinimumRecommendations(products, config.minimum_items)) {
		const fallback = await loadRecentProductFallback(config);
		products = fallback.products;
		productActions = fallback.actions;
		metadata = analyticsMetadata({}, config);
	}
	if (!hasMinimumRecommendations(products, config.minimum_items)) {
		throw new Error("Not enough displayable recommendations");
	}

	const track = root.querySelector("[data-luigi-client-track]");
	track.replaceChildren(...products.map((product, index) => (
		productCard(root, product, metadata, config, index)
	)));
	root.hidden = false;
	window.requestAnimationFrame(() => updateSliderControls(root));
	trackRecommendationView(products, metadata);

	if (!productActions) {
		productActions = await requestProductActions(products, config);
	}
	products.forEach((product, index) => {
		const placeholder = track.querySelector(`[data-luigi-client-action="${index}"]`);
		if (!placeholder) return;
		placeholder.replaceWith(productButton(
			root,
			product,
			metadata,
			config,
			productActions.get(String(index)),
		));
	});
	root.querySelector("[data-luigi-client-status]").textContent = `${products.length} recommendations loaded`;
	root.setAttribute("aria-busy", "false");
}

function bindControls(root) {
	const track = root.querySelector("[data-luigi-client-track]");
	let controlFrame = null;
	const scheduleControlUpdate = () => {
		if (controlFrame !== null) return;
		controlFrame = window.requestAnimationFrame(() => {
			controlFrame = null;
			updateSliderControls(root);
		});
	};
	const move = (direction) => {
		const card = track.querySelector(".flux-luigi-client__card");
		if (!card) return;
		const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
		track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: "smooth" });
	};
	root.querySelector("[data-luigi-client-prev]")?.addEventListener("click", () => move(-1));
	root.querySelector("[data-luigi-client-next]")?.addEventListener("click", () => move(1));
	track.addEventListener("scroll", scheduleControlUpdate, { passive: true });
	if ("ResizeObserver" in window) {
		new ResizeObserver(scheduleControlUpdate).observe(track);
	} else {
		window.addEventListener("resize", scheduleControlUpdate, { passive: true });
	}
}

function initialize(root) {
	bindControls(root);
	const load = () => loadRecommendations(root).catch(() => {
		const config = JSON.parse(root.dataset.config || "{}");
		if (config.hide_when_unavailable) {
			root.querySelector("[data-luigi-client-status]").textContent = "No recommendations available";
			root.setAttribute("aria-busy", "false");
			root.hidden = true;
			return;
		}
		const track = root.querySelector("[data-luigi-client-track]");
		const error = document.createElement("p");
		error.className = "flux-luigi-client__error";
		error.textContent = "Recommendations are temporarily unavailable.";
		track.replaceChildren(error);
		root.querySelector("[data-luigi-client-status]").textContent = error.textContent;
		root.setAttribute("aria-busy", "false");
	});

	if (!("IntersectionObserver" in window)) {
		load();
		return;
	}
	const observer = new IntersectionObserver((entries) => {
		if (!entries.some((entry) => entry.isIntersecting)) return;
		observer.disconnect();
		load();
	}, { rootMargin: "300px 0px" });
	observer.observe(root);
}

export function luigiClientRecommendationEvents() {
	document.querySelectorAll("[data-luigi-client-recommendations]").forEach(initialize);
}
